import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridPager, type KstDataGridHeaderButton, type KstDataGridRowAction } from '@/components/KstDataGrid';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddCompanyDocumentModal, type AddCompanyDocumentSaveData } from '@/components/modals/AddCompanyDocumentModal';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { hasValue } from '@/utils/legacy-utils';
import moment from 'moment';
import { AccessAuthorities } from '@/models';
import { uploadBlockBlob } from '@/services/azure-blob-service';
import { openPage } from '@/utils/dom';
// Constructors and values: access lazily via (window as any).X at usage sites
import { getAvailableSubmodules } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';

const companyDocumentsColumns: KstDataGridColumn[] = [
  { field: 'id', name: 'ID', size: 'hidden' },
  {
    field: 'title',
    name: 'Name / Download',
    size: 'col-xs-3',
    template: (e: any) => {
      return `<a href="${e.signedURL}" target="_blank">${e.title}</a>`;
    },
  },
  { field: 'filename', name: 'Filename', size: 'col-xs-3' },
  {
    field: 'dateCreated',
    name: 'Uploaded At',
    size: 'col-xs-3',
    template: (e: any) => {
      return moment(e.dateCreated).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT).toString();
    },
  },
  { field: 'createdBy', name: 'Uploaded By', size: 'col-xs-3' },
];

const headerButtons: KstDataGridHeaderButton[] = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

const rowActions: KstDataGridRowAction[] = [
  { name: 'Download', icon: 'fa-download', event: 'download', class: 'btn-primary', mobileOnly: true },
];

export function CompanyDocsScreen() {
  const { theme } = useTheme();

  const [entries, setEntries] = useState<any[] | null>(null);
  const [doesDataExist, setDoesDataExist] = useState(true);
  const [isUpdatePermissioned, setIsUpdatePermissioned] = useState(false);
  const [isDocsLoading, setIsDocsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Add modal
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Delete confirm modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmItems, setDeleteConfirmItems] = useState<any[]>([]);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');

  const rows: KstDataGridRows = useMemo(() => ({ entries }), [entries]);
  const pager: KstDataGridPager = useMemo(
    () => ({ curPage: null as any, numPages: null as any, records: null as any, isVisible: false }),
    []
  );

  const gridButtons = useMemo(
    () => (isUpdatePermissioned ? headerButtons : []),
    [isUpdatePermissioned]
  );

  const refreshCompanyDocumentsList = useCallback(() => {
    setIsDocsLoading(true);
    api.getCompanyDocumentsList((result: any) => {
      const list = result.attachmentList || [];
      setEntries(list);
      setDoesDataExist(list.length > 0);
      setIsDocsLoading(false);
    }, () => {
      App.showPopupNotification('Error getting company documents. Please try again.', 'error');
      setIsDocsLoading(false);
    });
  }, []);

  // Check permissions and load on mount
  useEffect(() => {
    const submodules = getAvailableSubmodules();
    const hasPermission = hasValue(submodules) ? isInArray(submodules, AccessAuthorities.ROLE_UPDATE_COMPANY_DOCUMENTS) : false;
    setIsUpdatePermissioned(hasPermission);
    refreshCompanyDocumentsList();
  }, [refreshCompanyDocumentsList]);

  const handleButtonCommand = useCallback((functionName: string, checkedItems: any[]) => {
    if (functionName === 'add') {
      setAddModalOpen(true);
    } else if (functionName === 'delete') {
      if (checkedItems.length === 0) {
        App.showPopupNotification('Please select a document to delete by clicking the checkbox.', 'warning');
        return;
      }
      const count = checkedItems.length;
      const message = count === 1
        ? 'Are you sure you want to delete this company document?'
        : `Are you sure you want to delete ${count} company documents?`;
      setDeleteConfirmItems(checkedItems);
      setDeleteConfirmMessage(message);
      setDeleteConfirmOpen(true);
    }
  }, []);

  const handleAddSave = useCallback((data: AddCompanyDocumentSaveData) => {
    const attachment = new (window as any).Attachment();
    attachment.title = data.documentName;
    attachment.filename = data.filename;

    setIsSaving(true);
    api.saveAttachment([attachment], (result: any) => {
      if (!result.azureStorageBlobInfoList?.length) {
        setIsSaving(false);
        App.showPopupNotification('Error preparing file upload.', 'error');
        return;
      }

      const blobInfo = result.azureStorageBlobInfoList[0];

      uploadBlockBlob(blobInfo.host, blobInfo.sasWriteToken, blobInfo.containerName, blobInfo.blobName, data.file).then(() => {
        const addDocumentData = {
          attachment: {
            id: blobInfo.attachmentId,
            activationKey: blobInfo.activationKey,
          },
        };

        api.addCompanyDocument(addDocumentData, () => {
          setIsSaving(false);
          setAddModalOpen(false);
          refreshCompanyDocumentsList();
        }, () => {
          setIsSaving(false);
          App.showPopupNotification('Error adding company document.', 'error');
        });
      }).catch(() => {
        setIsSaving(false);
        App.showPopupNotification('Error uploading file.', 'error');
      });
    });
  }, [refreshCompanyDocumentsList]);

  const handleConfirmDelete = useCallback(() => {
    const checkedItems = deleteConfirmItems;
    let count = 0;
    const len = checkedItems.length;

    setIsDeleting(true);
    for (let i = 0; i < len; i++) {
      api.removeCompanyDocument(checkedItems[i].id, () => {
        count++;
        if (count === len) {
          setIsDeleting(false);
          refreshCompanyDocumentsList();
          setDeleteConfirmOpen(false);
          setDeleteConfirmItems([]);
          setDeleteConfirmMessage('');
        }
      }, () => {
        setIsDeleting(false);
        App.showPopupNotification('Error removing company document.', 'error');
      });
    }
  }, [deleteConfirmItems, refreshCompanyDocumentsList]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, []);

  const handleRowAction = useCallback(({ action, row }: { action: string; row: any }) => {
    if (action === 'download' && row.signedURL) {
      openPage(row.signedURL, true);
    }
  }, []);

  return (
    <div className={`company-docs-screen ${theme}`}>
      <AddCompanyDocumentModal
        open={addModalOpen}
        saving={isSaving}
        theme={theme}
        onSave={handleAddSave}
        onCancel={() => setAddModalOpen(false)}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Company Document"
        message={deleteConfirmMessage}
        confirmText="Delete"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">Company Documents</h3>
                {isUpdatePermissioned && (
                  <p className="kst-page-description">
                    Add company-wide documents for quick access.
                  </p>
                )}
                {!isUpdatePermissioned && !doesDataExist && (
                  <p className="kst-page-description">
                    Company-wide documents will appear below when uploaded.
                  </p>
                )}
              </div>

              <KstDataGrid
                columns={companyDocumentsColumns}
                rows={rows}
                headerbuttons={gridButtons}
                rowactions={rowActions}
                pager={pager}
                showcheckbox={isUpdatePermissioned}
                showheader={isUpdatePermissioned}
                mobiletitlefield="title"
                mobileactionsonly={true}
                loading={isDocsLoading}
                onButtonCommand={handleButtonCommand}
                onRowAction={handleRowAction}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyDocsScreen;

